import { ExprValue } from '../ExprValue';
import { EvalOper } from './EvalOper';
export var EvalState;
(function (EvalState) {
    function make(items) {
        return { items, values: new Map() };
    }
    EvalState.make = make;
    function reducer(initial, exprNode) {
        switch (exprNode.type) {
            case "NULL_LITERAL":
                return vNullLiteral(initial, exprNode);
            case "BOOLEAN_LITERAL":
                return vBooleanLiteral(initial, exprNode);
            case "NUMBER_LITERAL":
                return vNumberLiteral(initial, exprNode);
            case "STRING_LITERAL":
                return vStringLiteral(initial, exprNode);
            case "DATE_TIME_LITERAL":
                return vDateTimeLiteral(initial, exprNode);
            case "ACCESS":
                return vAccess(initial, exprNode);
            case "CALL":
                return vCall(initial, exprNode);
            case "CASE":
                return vCase(initial, exprNode);
            case "CASE_ELSE":
                return vCaseElse(initial, exprNode);
            case "IF_ELSE":
                return vIfElse(initial, exprNode);
            case "IF_COND":
                return vIfCond(initial, exprNode);
            case "UNARY":
                return vUnary(initial, exprNode);
            case "BINARY":
                return vBinary(initial, exprNode);
            case "LOGICAL":
                return vLogical(initial, exprNode);
            default:
                return initial;
        }
    }
    EvalState.reducer = reducer;
    function getValue(evalState, exprNode, index) {
        const values = evalState.values.get(exprNode.id);
        if (values === undefined) {
            return undefined;
        }
        const value = values[index];
        if (value === undefined) {
            return undefined;
        }
        return value;
    }
    EvalState.getValue = getValue;
    function getValues(evalState, exprNode) {
        return evalState.values.get(exprNode.id);
    }
    function setValues(evalState, exprNode, values) {
        evalState.values.set(exprNode.id, values);
        return evalState;
    }
    function vNullLiteral(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            values[itemIdx] = null;
        }
        return setValues(initial, exprNode, values);
    }
    function vBooleanLiteral(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            values[itemIdx] = exprNode.value;
        }
        return setValues(initial, exprNode, values);
    }
    function vNumberLiteral(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            values[itemIdx] = exprNode.value;
        }
        return setValues(initial, exprNode, values);
    }
    function vStringLiteral(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            values[itemIdx] = exprNode.value;
        }
        return setValues(initial, exprNode, values);
    }
    function vDateTimeLiteral(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            values[itemIdx] = exprNode.value;
        }
        return setValues(initial, exprNode, values);
    }
    function vAccess(initial, exprNode) {
        if (exprNode.path.length !== 1) {
            throw new Error(`Access expressions require path with exactly one part ExprNode(${exprNode.id}) ${JSON.stringify(exprNode.path, null, 2)}`);
        }
        const { items } = initial;
        const fieldName = exprNode.path[0].name;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            const value = items[itemIdx][fieldName];
            values[itemIdx] = value === undefined ? null : value;
        }
        return setValues(initial, exprNode, values);
    }
    function vCall(initial, exprNode) {
        const { call, args } = exprNode;
        return setValues(initial, exprNode, getExprCallValues(initial, call, args));
    }
    function vCase(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        const condValues = getValues(initial, exprNode.value) || [];
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            let match = false;
            let result;
            for (const cond of exprNode.conds) {
                const value = getValue(initial, cond.when, itemIdx);
                if (value === undefined) {
                    break;
                }
                if (ExprValue.eq(condValues[itemIdx], value)) {
                    match = true;
                    result = getValue(initial, cond.then, itemIdx);
                    break;
                }
            }
            if (match === false) {
                if (exprNode.else == null) {
                    result = null;
                }
                else {
                    result = getValue(initial, exprNode.else, itemIdx);
                }
            }
            if (result !== undefined) {
                values[itemIdx] = result;
            }
            else {
                throw new Error(`CASE expression result is undefined ${exprNode.id}`);
            }
        }
        return setValues(initial, exprNode, values);
    }
    function vCaseElse(initial, node) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            const result = getValue(initial, node.then, itemIdx);
            if (result !== undefined) {
                values[itemIdx] = result;
            }
            else {
                throw new Error(`CaseElse expression result is undefined ${node.then.id}`);
            }
        }
        return setValues(initial, node, values);
    }
    function vIfElse(initial, exprNode) {
        const { items } = initial;
        const { base, conds } = exprNode;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            let match = false;
            let result;
            const baseCond = ExprValue.toBool(getValue(initial, base.cond, itemIdx));
            if (baseCond === true) {
                match = true;
                result = getValue(initial, base.result, itemIdx);
            }
            else {
                for (const cond of conds) {
                    const condValue = ExprValue.toBool(getValue(initial, cond.cond, itemIdx));
                    if (condValue === true) {
                        match = true;
                        result = getValue(initial, cond.result, itemIdx);
                        break;
                    }
                }
            }
            if (match === false) {
                if (exprNode.else == null) {
                    result = null;
                }
                else {
                    result = getValue(initial, exprNode.else, itemIdx);
                }
            }
            if (result !== undefined) {
                values[itemIdx] = result;
            }
            else {
                throw new Error(`IF/ELSE expression result is undefined ${exprNode.id}`);
            }
        }
        return setValues(initial, exprNode, values);
    }
    function vIfCond(initial, exprNode) {
        const { items } = initial;
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            let result;
            const condValue = ExprValue.toBool(getValue(initial, exprNode.cond, itemIdx));
            if (condValue === true) {
                result = getValue(initial, exprNode.result, itemIdx);
            }
            else {
                result = null;
            }
            if (result !== undefined) {
                values[itemIdx] = result;
            }
            else {
                throw new Error(`IF/ELSE expression result is undefined ${exprNode.id}`);
            }
        }
        return setValues(initial, exprNode, values);
    }
    function vUnary(initial, exprNode) {
        const { items } = initial;
        const { operator, operand } = exprNode;
        const operands = getValues(initial, operand) || [];
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            const itemOperand = operands[itemIdx];
            switch (operator) {
                case 'NOT':
                    values[itemIdx] = EvalOper.not(itemOperand);
                    break;
                case '-': {
                    if (typeof itemOperand === 'number' ||
                        ExprValue.isDecimal(itemOperand)) {
                        values[itemIdx] = EvalOper.neg(itemOperand);
                    }
                    else {
                        throw new Error(`Cannot negate non-numeric value ${itemOperand}`);
                    }
                    break;
                }
            }
        }
        return setValues(initial, exprNode, values);
    }
    function vBinary(initial, exprNode) {
        const { items } = initial;
        const { operator, left, right } = exprNode;
        const lefts = getValues(initial, left) || [];
        const rights = getValues(initial, right) || [];
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            const leftOperand = lefts[itemIdx];
            const rightOperand = rights[itemIdx];
            switch (operator) {
                case '+':
                case '-':
                case '*':
                case '/':
                case '%': {
                    if (leftOperand === null || rightOperand === null) {
                        values[itemIdx] = null;
                        break;
                    }
                    if (ExprValue.isDecimalSource(leftOperand) &&
                        ExprValue.isDecimalSource(rightOperand)) {
                        values[itemIdx] = EvalOper.mathematic(operator, leftOperand, rightOperand);
                        break;
                    }
                    throw new Error(`Cannot evaluate expression ${leftOperand} ${operator} ${rightOperand}`);
                }
                case '=':
                case '<>':
                case '<':
                case '<=':
                case '>':
                case '>=': {
                    values[itemIdx] = EvalOper.relational(operator, leftOperand, rightOperand);
                    break;
                }
                case 'IS':
                case 'IS NOT': {
                    if (rightOperand !== null) {
                        throw new Error(`Cannot evaluate expression ${leftOperand} ${operator} ${rightOperand}, ` +
                            `the right hand side expression must be NULL`);
                    }
                }
                case 'IS DISTINCT FROM':
                case 'IS NOT DISTINCT FROM': {
                    values[itemIdx] = EvalOper.identity(operator, leftOperand, rightOperand);
                    break;
                }
                default:
                    throw new Error(`Cannot evaluate binary expression with operator ${operator}`);
            }
        }
        return setValues(initial, exprNode, values);
    }
    function vLogical(initial, exprNode) {
        const { items } = initial;
        const { operator, left, right } = exprNode;
        const lefts = getValues(initial, left) || [];
        const rights = getValues(initial, right) || [];
        const values = new Array(items.length);
        for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
            const leftOperand = lefts[itemIdx];
            const rightOperand = rights[itemIdx];
            switch (operator) {
                case 'OR':
                case 'AND': {
                    values[itemIdx] = EvalOper.logical(operator, leftOperand, rightOperand);
                    break;
                }
                default:
                    throw new Error(`Cannot evaluate logical expression with unknown operator ${operator}`);
            }
        }
        return setValues(initial, exprNode, values);
    }
    function getExprCallValues(initial, exprCall, exprArgs) {
        const { items } = initial;
        switch (exprCall.stage) {
            case "CONST": {
                const consts = new Array(items.length);
                for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
                    consts[itemIdx] = exprCall.value();
                }
                return consts;
            }
            case "SCALAR": {
                const scalars = new Array(items.length);
                for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
                    scalars[itemIdx] = exprCall.value(...getScalarArgValues(initial, exprArgs, itemIdx));
                }
                return scalars;
            }
            case "ANALYTIC": {
                const values = [];
                const { items: { length }, } = initial;
                let state = exprCall.init == null ? {} : exprCall.init();
                for (let index = 0; index < items.length; index++) {
                    const frame = { index, length };
                    const callValue = exprCall.value({ frame, state, values }, ...getAnalyticArgValues(initial, exprArgs));
                    state = callValue.state;
                    values.push(callValue.value);
                }
                return values;
            }
            default: {
                throw new Error(`Aggregate expressions not allowed in process evaluation`);
            }
        }
    }
    function getScalarArgValues(initial, exprNodes, itemIdx) {
        const values = [];
        for (const exprNode of exprNodes) {
            const value = getValue(initial, exprNode, itemIdx);
            if (value === undefined) {
                throw new Error(`Expression value is undefined ${exprNode.id}`);
            }
            values.push(value);
        }
        return values;
    }
    function getAnalyticArgValues(initial, exprNodes) {
        const values = [];
        for (const exprNode of exprNodes) {
            const argValue = getValues(initial, exprNode);
            if (argValue === undefined) {
                throw new Error(`Expression value is undefined ${exprNode.id}`);
            }
            values.push(argValue);
        }
        return values;
    }
})(EvalState || (EvalState = {}));
